/* ActionsRenderer.js */

"use strict";
const _ = require("lodash");
const PopUpWidget = require("@gluenetworks/steve_ui_widgets").PopUp;
class ActionsColumnRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("actions-column");
        this._rootElem.component = this._model;
        this._viewMoreIcon = this._createViewMoreIcon();
        this._popUpWidget = undefined;

        this._registerListeners();
        this.render();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this._model.on("ViewMore", (this._onViewMore = this._onViewMore.bind(this)));
        this._model.on("UpdateTooltip", (this._onUpdateTooltip = this._onUpdateTooltip.bind(this)));
    }

    _registerPopUpListener() {
        this._popUpWidget.on("PopUpVisible", (this._onContent = this._onContent.bind(this)));
    }

    _unregisterListeners() {
        this._model.removeAllListeners("ViewMore", this._onViewMore);
        this._model.removeAllListeners("UpdateTooltip", this._onUpdateTooltip);
    }

    _unregisterPopUpListener() {
        if (!_.isEmpty(this._popUpWidget)) {
            this._popUpWidget.removeListener("PopUpVisible", this._onContent);
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        this._rootElem.innerHTML = "";
        this._rootElem.appendChild(this._viewMoreIcon);

        this._setViewMore(this._model.getViewMore());

        return Promise.resolve();
    }

    shutdown() {
        this._rootElem.remove();
        this._rootElem = null;
        this._unregisterPopUpListener();
        return Promise.all([_.invoke(this._popUpWidget, "shutdown")]).then(() => {
            this._unregisterListeners();
            this._model = undefined;
            this._viewMoreIcon.remove();
            this._viewMoreIcon = undefined;
            this._rootElem = undefined;
            this._popUpWidget = undefined;
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START HANDLER METHODS                   */
    /*******************************************/
    _onViewMore(newVal) {
        this._setViewMore(newVal);
    }

    _onUpdateTooltip() {
        this._model.getAllLogs(this._model.getViewMore().logId).then((logs) => this._updateTooltip(logs));
        this._scrollToEnd();
    }
    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/
    _setViewMore(viewMore) {
        this._viewMoreIcon.classList.toggle("hidden", !viewMore);

        if (viewMore) {
            this._model.getAllLogs(viewMore.logId).then((logs) => this._setLogData(logs));
        }
    }

    _setLogData(logs) {
        if (this._rootElem === null) {
            return;
        }

        let viewMoreContentElement = document.createElement("view-more-pop-up");
        viewMoreContentElement.classList.add("activity-log-popup");
        viewMoreContentElement.innerHTML = this._getToolTipContent(logs);
        this._popUpWidget = new PopUpWidget({
            content: viewMoreContentElement.outerHTML,
            anchor: this._viewMoreIcon,
            anchorPoint: "left",
            displayDirection: "bottomLeft",
            openOnHover: true,
            parent: viewMoreContentElement.parentNode
        });

        this._registerPopUpListener();
    }

    _onContent() {
        this._scrollToEnd();
    }

    _getToolTipContent(logs) {
        let wrapper = document.createElement("div");
        _.forEach(logs, (log) => {
            wrapper.appendChild(this._createLogItem(log));
        });
        return `<span class="arrow"><i></i></span><div class="main-container p-b-0">${wrapper.outerHTML}</div>`;
    }
    _createLogItem(log) {
        let logEntry = document.createElement("p");

        let logLineNumber = document.createElement("span");
        logLineNumber.classList.add("log-line-number");
        logLineNumber.innerText = _.get(log, "lln", "");

        let timestamp = _.get(log, "time", "");
        let fileTimeStamp = timestamp;

        if (timestamp) {
            fileTimeStamp = Date.create(timestamp).format("{yyyy}-{MM}-{dd}T{HH}:{mm}:{ss}.{fff}");
            timestamp = Date.create(timestamp).format("{HH}:{mm}:{ss}.{fff}");
        }

        let time = document.createElement("span");
        time.classList.add("timestamp");
        time.innerText = timestamp;
        if (!this._logFileStarttime) {
            this._logFileStarttime = fileTimeStamp;
        }
        let level = document.createElement("span");
        level.classList.add("level");
        let lvl = "";
        if (_.get(log, "resp", "")) {
            lvl = "RESP";
        } else if (_.get(log, "eventType", false)) {
            if (_.get(log, "eventType") === "START" || _.get(log, "eventType") === "FINISH") {
                lvl = _.get(log, "eventType");
            } else {
                lvl = "EVENT";
            }
        }
        level.innerText = lvl;

        let message = document.createElement("span");
        message.classList.add("message");
        message.innerText = _.get(log, "msg", _.get(log, "resp", ""));

        logEntry.appendChild(logLineNumber);
        logEntry.appendChild(time);
        logEntry.appendChild(level);
        logEntry.appendChild(message);
        return logEntry;
    }

    _createViewMoreIcon() {
        const viewMoreIcon = document.createElement("actionable-icon-gl");
        viewMoreIcon.dataset.icon = "configdrift.view_more";
        viewMoreIcon.setAttribute("title", "View Details");
        viewMoreIcon.classList.add("hidden");
        return viewMoreIcon;
    }

    _updateTooltip(logs) {
        let viewMoreContentElement = document.createElement("view-more-pop-up");
        viewMoreContentElement.classList.add("activity-log-popup");
        viewMoreContentElement.innerHTML = this._getToolTipContent(logs);
        this._popUpWidget.setContent(viewMoreContentElement.outerHTML);
    }

    _scrollToEnd() {
        const _viewMorePopUpContainer = document.querySelectorAll("view-more-pop-up");
        if (!_.isEmpty(_viewMorePopUpContainer)) {
            _viewMorePopUpContainer.forEach((currentContainer) => {
                currentContainer.scrollTop = currentContainer.scrollHeight;
            });
        }
    }
    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = ActionsColumnRenderer;
